// Types and Data for Self-Determination Theory (SDT) Motivation survey
// Inspired by Deci & Ryan's Self-Determination Theory - original implementation for GROWNi.sk

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'

export type SDTSurveyId = string

export interface SDTSurvey {
    id: SDTSurveyId
    surveyType: SurveyTypes.SDT
    userId: UserId
    hash?: string
    questions: SDTQuestionType[]
    version?: 2
    contextProfiles?: SDTContextProfile[]
    // Legacy fields retained to read versionless records; absent in version 2.
    needScores?: NeedScores // 0-100 per need
    motivationScores?: MotivationScores // 0-100 per motivation type
    dominantNeed?: PsychologicalNeed
    motivationType?: MotivationType
    overallWellbeing?: number // 0-100
    confidence?: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Three basic psychological needs
export enum PsychologicalNeed {
    AUTONOMY = 'autonomy', // Autonómia
    COMPETENCE = 'competence', // Kompetencia
    RELATEDNESS = 'relatedness' // Spolupatričnosť
}

// Motivation types on the self-determination continuum
export enum MotivationType {
    AMOTIVATION = 'amotivation', // Amotivácia
    EXTERNAL = 'external', // Externí regulácia
    INTROJECTED = 'introjected', // Introjetovaná regulácia
    IDENTIFIED = 'identified', // Identifikovaná regulácia
    INTEGRATED = 'integrated', // Integrovaná regulácia
    INTRINSIC = 'intrinsic' // Vnútorná motivácia
}

export const PsychologicalNeedLabels: Record<PsychologicalNeed, string> = {
    [PsychologicalNeed.AUTONOMY]: 'Autonómia',
    [PsychologicalNeed.COMPETENCE]: 'Kompetencia',
    [PsychologicalNeed.RELATEDNESS]: 'Spolupatričnosť'
}

export const PsychologicalNeedDescriptions: Record<PsychologicalNeed, string> = {
    [PsychologicalNeed.AUTONOMY]: 'Potreba byť autorom vlastného konania, robiť veci dobrovoľne a zo slobodnej vôle',
    [PsychologicalNeed.COMPETENCE]: 'Potreba cítiť sa schopný/schopná a efektívny/efektívna v tom, čo robíš',
    [PsychologicalNeed.RELATEDNESS]: 'Potreba cítiť sa prepojený/prepojená s ostatnými a patriť k skupine'
}

export const MotivationTypeLabels: Record<MotivationType, string> = {
    [MotivationType.AMOTIVATION]: 'Amotivácia',
    [MotivationType.EXTERNAL]: 'Externá regulácia',
    [MotivationType.INTROJECTED]: 'Introjetovaná regulácia',
    [MotivationType.IDENTIFIED]: 'Identifikovaná regulácia',
    [MotivationType.INTEGRATED]: 'Integrovaná regulácia',
    [MotivationType.INTRINSIC]: 'Vnútorná motivácia'
}

export const MotivationTypeDescriptions: Record<MotivationType, string> = {
    [MotivationType.AMOTIVATION]: 'Nevieš, prečo to robíš. Necítiš význam ani kontrolu nad situáciou.',
    [MotivationType.EXTERNAL]: 'Robíš to kvôli odmenám, trestom alebo tlaku zvonka.',
    [MotivationType.INTROJECTED]: 'Robíš to kvôli pocitom viny, hanby alebo snahe o sebavedomie.',
    [MotivationType.IDENTIFIED]: 'Robíš to, pretože si uvedomuješ hodnotu a osobný význam aktivity.',
    [MotivationType.INTEGRATED]: 'Aktivita je plne zosúladená s tvojimi hodnotami a identitou.',
    [MotivationType.INTRINSIC]: 'Robíš to pre radosť, záujem a vnútorné uspokojenie.'
}

export interface SDTQuestionType {
    id: string
    context: string // "Prečo cvičíš?", "Prečo sa učíš?"
    need?: PsychologicalNeed // Which need this measures (for need satisfaction questions)
    options?: SDTOption[] // Legacy single-choice records
    domain?: SDTDomain
    motivationType?: MotivationType
    type?: 'scale'
    value?: string | number // Legacy option ID, rating 1–5, or not_applicable
}

export interface SDTOption {
    id: string
    text: string
    motivationType: MotivationType
    needSatisfaction?: Partial<Record<PsychologicalNeed, number>> // 0-10 for each need
}

export interface NeedScores {
    [PsychologicalNeed.AUTONOMY]: number // 0-100
    [PsychologicalNeed.COMPETENCE]: number
    [PsychologicalNeed.RELATEDNESS]: number
}

export interface MotivationScores {
    [MotivationType.AMOTIVATION]: number // 0-100
    [MotivationType.EXTERNAL]: number
    [MotivationType.INTROJECTED]: number
    [MotivationType.IDENTIFIED]: number
    [MotivationType.INTEGRATED]: number
    [MotivationType.INTRINSIC]: number
}

// Version 2 is a custom reflection tool, not a validated psychological assessment.
export type SDTDomain = 'work_study' | 'learning' | 'helping'
export const SDT_NOT_APPLICABLE = 'not_applicable'
export const SDT_DOMAINS: Record<SDTDomain, { label: string; prompt: string }> = {
    work_study: {
        label: 'Práca alebo štúdium',
        prompt: 'Spomeň si na bežný deň v práci alebo škole za posledný mesiac. Nakoľko na teba sedia tieto dôvody, prečo sa venuješ svojim úlohám?'
    },
    learning: {
        label: 'Učenie sa niečoho nového',
        prompt: 'Predstav si niečo, čo sa práve učíš mimo bežných povinností — napríklad jazyk, zručnosť alebo hobby. Čo ťa k tomu privádza?'
    },
    helping: {
        label: 'Pomoc druhým',
        prompt: 'Spomeň si, keď si za posledný mesiac venoval/a čas pomoci niekomu alebo dobrovoľníctvu. Aké dôvody pri tom zohrávali úlohu?'
    }
}

export interface SDTContextProfile {
    domain: SDTDomain
    ratings: Partial<Record<MotivationType, number>> // Independent ratings, 1–5; never percentages.
}

export const SDTReasonLabels: Record<MotivationType, string> = {
    [MotivationType.AMOTIVATION]: 'Nejasný dôvod',
    [MotivationType.EXTERNAL]: 'Odmeny a očakávania',
    [MotivationType.INTROJECTED]: 'Pocit povinnosti',
    [MotivationType.IDENTIFIED]: 'Osobný význam',
    [MotivationType.INTEGRATED]: 'Súlad s hodnotami',
    [MotivationType.INTRINSIC]: 'Záujem a radosť'
}

const reasons: Record<SDTDomain, Array<[MotivationType, string]>> = {
    work_study: [
        [MotivationType.EXTERNAL, 'Výplata, známky alebo iné odmeny sú pre mňa dôvodom venovať sa týmto úlohám.'],
        [MotivationType.INTROJECTED, 'Keď sa úlohám nevenujem, mám výčitky, aj keď odo mňa práve nikto nič nežiada.'],
        [MotivationType.IDENTIFIED, 'Tieto úlohy mi pomáhajú dosiahnuť niečo, na čom mi osobne záleží.'],
        [MotivationType.INTRINSIC, 'Samotná činnosť ma zaujíma, aj keď za ňu práve neprichádza odmena.'],
        [MotivationType.AMOTIVATION, 'Pri týchto úlohách si často nie som istý/á, prečo ich vlastne robím.']
    ],
    learning: [
        [
            MotivationType.EXTERNAL,
            'Do učenia sa púšťam aj preto, že to odo mňa očakávajú iní alebo za to môžem získať uznanie.'
        ],
        [MotivationType.INTROJECTED, 'Ak sa neposúvam, mám pocit, že by som sa mal/a viac snažiť.'],
        [MotivationType.IDENTIFIED, 'Táto zručnosť sa mi zíde pri niečom, čo chcem uskutočniť.'],
        [MotivationType.INTRINSIC, 'Baví ma skúšať nové veci a prichádzať na to, ako fungujú.'],
        [MotivationType.AMOTIVATION, 'V učení pokračujem, hoci mi momentálne nie je jasné, čo mi prináša.']
    ],
    helping: [
        [
            MotivationType.EXTERNAL,
            'Pri pomoci druhým zohráva úlohu aj to, čo odo mňa očakáva okolie alebo ako ma vníma.'
        ],
        [MotivationType.INTROJECTED, 'Ťažko sa mi odmieta pomoc, pretože by som potom mal/a výčitky.'],
        [MotivationType.IDENTIFIED, 'Záleží mi na tom, čo moja pomoc pre druhého človeka alebo komunitu znamená.'],
        [MotivationType.INTRINSIC, 'Samotná činnosť, ktorou pomáham, ma baví.'],
        [MotivationType.AMOTIVATION, 'Niekedy pomáham zo zvyku a neviem presne povedať, prečo v tom pokračujem.']
    ]
}

export const SDT_QUESTIONS: SDTQuestionType[] = (Object.keys(reasons) as SDTDomain[]).flatMap((domain) =>
    reasons[domain].map(([motivationType, context]) => ({
        id: `sdt-v2-${domain}-${motivationType}`,
        domain,
        motivationType,
        context,
        type: 'scale' as const
    }))
)

export function shuffleSDTItems<T>(items: T[]): T[] {
    const shuffled = [...items]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]!
        shuffled[i] = shuffled[j]!
        shuffled[j] = left
    }
    return shuffled
}

// Shuffle within contexts once; the order is persisted with draft answers.
export function initializedSDTSurvey(userId: UserId): Omit<SDTSurvey, 'id' | 'createdAt'> {
    return {
        surveyType: SurveyTypes.SDT,
        userId,
        hash: uuidv4(),
        version: 2,
        questions: (Object.keys(SDT_DOMAINS) as SDTDomain[]).flatMap((domain) =>
            shuffleSDTItems(SDT_QUESTIONS.filter((q) => q.domain === domain)).map((q) => ({ ...q }))
        ),
        isDraft: true,
        deletedAt: null
    }
}
